/* eslint-disable no-console */
import { action, makeAutoObservable, observable, runInAction } from 'mobx'
import ReportData from '../data/ReportData'
import {
  fetchChartListBySourceId,
  generateReportSourceById,
  getReportSourceById,
  putReportSourceById,
} from '@services/api/wizardAPI'
import {
  IReportBlueprint,
  IReportBlueprintChart,
  IReportComponentBlueprint,
} from '../services/decoders/blueprintDecoder'
import rootStore from './RootStore'
import { v4 as uuid } from 'uuid'
import { cloneDeep } from 'lodash'
import { fetchBlueprints } from '@services/api/blueprintAPI'

export interface IAvaliableDrilldown {
  name: string
  data: IDrilldownMode[]
}

interface IDrilldownMode {
  label: string
  type: 0 | 1 | 2
  description: string
  value: string
  id: string
}

interface IMetadata {
  reportName: string;
  shortDescription: string;
  internalNotes: string;
}

export const getDrilldownModes = (): IDrilldownMode[] => [
  {
    label: 'Chart',
    type: 0,
    description:
      'Select a drilldown that should be shown if clicked anywhere in the chart',
    value: 'chart',
    id: 'Chart',
  },
  {
    label: 'Columns',
    description: 'Assign drilldowns to be opened if clicked in columns',
    value: 'columns',
    id: 'Columns',
    type: 1,
  },
  {
    label: 'Rows',
    description: 'Assign drilldowns to be opened if clicked in rows',
    value: 'rows',
    id: 'Rows',
    type: 2,
  },
]
class WizardStore {
  metadata: IMetadata = {
    reportName: '',
    shortDescription: '',
    internalNotes: ''
  }
  reportTitle = ''
  messages = new Map()
  selectedNodeId = undefined
  currentStep = 1
  error
  canGenerate = false
  saved = false
  selectedSection = undefined
  loadingChartSeries = true
  chartSeries: {
    columns: string[]
    rows: string[]
    chart: string[]
  }[] = [{ columns: [], rows: [], chart: [''] }]

  savingStructure = false
  availableDrilldowns = [
    {
      name: 'Drilldown',
      data: [{ id: '1', label: '---' }],
    },
  ] as IAvaliableDrilldown[]
  drilldownModeSelected = getDrilldownModes()[0]
  sourceId = ''

  /**
   * Blueprints
   */
  availableBlueprints = null // array
  selectedBlueprint = null
  loadingBlueprints = true

  /**
   * Report Structure
   * @type {boolean}
   */
  loadingStructure = true
  structureData = {}
  availableCharts = []
  reportBlueprint = {} as unknown as IReportBlueprint

  constructor() {
    makeAutoObservable(this, {
      structureData: observable,
      availableCharts: observable,
      setAvailableData: action,
      reportTitle: observable,
      setError: action,
      setCanGenerate: action,
      setSourceId: action,
      addNode: action,
    })

    this.structureData = {
      optionsAvailable: {},
      reportStructure: ReportData,
    }

    this.reportTitle = 'Hard Coded Title - PABS'
  }

  invalidateReportWizard = () => {
    this.reportTitle = ''
    this.selectedNodeId = undefined
    this.currentStep = 1
    this.error = undefined // Should be a string
    this.saved = false
    this.selectedSection = undefined
    this.loadingChartSeries = true
    this.chartSeries = [{ columns: [], rows: [], chart: [''] }]
    this.savingStructure = false
    this.availableDrilldowns = [
      {
        name: 'Drilldown',
        data: [{ id: '1', label: '---' }],
      },
    ] as IAvaliableDrilldown[]
    this.drilldownModeSelected = getDrilldownModes()[0]
    this.sourceId = ''
    this.availableBlueprints = null // array
    this.selectedBlueprint = null
    this.loadingBlueprints = true
    this.loadingStructure = true
    this.structureData = {}
    this.availableCharts = []
    this.reportBlueprint = {} as unknown as IReportBlueprint

    rootStore.dropzoneStore.file = null
    rootStore.dropzoneStore.uploadError = null
  }

  get reportSections() {
    return this.reportBlueprint?.children?.map(
      (section: IReportComponentBlueprint) => {
        return {
          title: section.name,
          id: section.id,
          filters: section.filters,
        }
      },
    )
  }

  fetchBlueprintList = async () => {
    try {
      this.loadingBlueprints = true
      const blueprintData = await fetchBlueprints()
      runInAction(() => {
        this.availableBlueprints = blueprintData
        this.selectedBlueprint = blueprintData[0].id
      })
    } catch (e) {
      this.error = (e as Error).message
      this.loadingBlueprints = false
    } finally {
      this.loadingBlueprints = false
    }
  }

  fetchStructureData = async urlSourceId => {
    try {
      if (!urlSourceId && !this.sourceId) {
        this.error = 'Source ID is required'
        return
      }
      this.loadingStructure = true
      this.setSourceId(urlSourceId)
      const reportBlueprint = await getReportSourceById(this.sourceId)
      if (!reportBlueprint?.components) {
        throw new Error('Invalid report blueprint')
      }

      const chartList = await fetchChartListBySourceId(this.sourceId)
      if (!chartList) {
        throw new Error('Invalid chart list')
      }

      const recursiveType = node => {
        if (node.children) {
          node.children = node.children.map(child => recursiveType(child))
          node.type = node.componentType === 1 ? 'subsection' : 'chart'
        }
        return node
      }

      runInAction(() => {
        /**
         * Remap the data to the reportBlueprint structure
         * @type {{children: *, id: string, lastModified: string, title: string, version: string}}
         */

        this.reportBlueprint = {
          ...reportBlueprint,
          children: (
            reportBlueprint.components as IReportComponentBlueprint[]
          ).map(component => ({
            ...component,
            componentType: 0,
            type: 'section',
            children: component.children.map(child => recursiveType(child)),
          })),
        }

        /**
         * Remap the data to the availableCharts structure
         */

        this.availableCharts = chartList.map(chart => ({
          ...chart,
          name: chart.chartCollectionName,
          id: uuid(),
          rows: chart.protoCharts[0].rows.map(row => row.name),
          columns: chart.protoCharts[0].columns.map(column => column.name),
          drillDownInfo: chart.protoCharts[0].drillDownInfo,
          type: 'chart',
        }))
      })
    } catch (e) {
      console.log(e)
      this.error = (e as Error).message
    } finally {
      this.loadingStructure = false
    }
  }

  /**
   * TODO: move it to utils
   * @param sectionId
   * @param filters
   * @param sectionTitle
   */
  updateReportSectionFilters = (sectionId, filters, sectionTitle) => {
    const filterSelection = filters.map(filter =>
      this.capitalizeFirstLetter(filter.id),
    )

    // TODO: this is just an example of error
    if (filterSelection.includes('TimePeriod')) {
      // TODO: add a VALID message to the UI
      this.setError(
        'TimePeriod filter is not allowed to the current section ID.',
      )
    } else {
      const section = this.reportBlueprint.children.find(
        (s: IReportComponentBlueprint) => s.id === sectionId,
      )

      if (!section) return

      section.name = sectionTitle ?? section.name
      section.filters = filters.map(filter =>
        this.capitalizeFirstLetter(filter.id),
      )
      this.reportBlueprint.children = [...this.reportBlueprint.children]
    }
  }

  capitalizeFirstLetter = string => {
    return string.charAt(0).toUpperCase() + string.slice(1)
  }

  setSaved = value => {
    this.saved = value
  }
  setError = value => {
    this.error = value
  }
  setCanGenerate = value => {
    console.log('Set CanGenerate: ' + value)
    this.canGenerate = value
  }
  setSourceId = value => {
    this.sourceId = value
  }
  get chartMessages() {
    if (!this.selectedNodeId) return {}
    return this.messages[this.selectedNodeId] || {}
  }

  setAvailableData = availables => {
    this.availableCharts = availables
  }

  setSelectedSection = section => {
    this.selectedSection = section
  }

  get currentChart() {
    return this.findNode(this.selectedNodeId, this.reportBlueprint.children)
  }

  generate = () => {
    this.setMessages()
  }

  setMessages = () => {
    this.messages = new Map()
    this.reportBlueprint.children.forEach((item, index) => {
      this.messages.set(item.id, {
        type: 'info',
        title: item.name,
        message: `Message ${index + 1}`,
      })
    })
  }

  loadChartSeries = async () => {
    /**
     * It must be updated every time the chart (selectedNodeId) changes
     * @type {[{},...*]}
     */
    this.availableDrilldowns = [
      {},
      ...this.getRemapChildrenToDropdown(this.currentChart.children),
    ]

    if (
      !this.selectedNodeId ||
      this.selectedNodeId !== this.currentChart.id ||
      this.chartSeries[this.selectedNodeId]
    )
      return

    try {
      if (
        !this.currentChart ||
        !this.currentChart.children ||
        !(
          this.currentChart?.type === 'chart' ||
          this.currentChart?.type?.startsWith('drilldown')
        )
      ) {
        throw new Error('Select a chart or drilldown')
      }

      /**
       * All the charts available in the current blueprint must have
       * the equivalent named chart in the availableCharts
       */
      const correspondingChart =
        this.availableCharts.find(
          (chart: IReportBlueprintChart) =>
            chart.name === this.currentChart.name,
        ) || ({} as unknown as IReportBlueprintChart)
      this.chartSeries[this.selectedNodeId] = {
        rows: correspondingChart.rows,
        columns: correspondingChart.columns,
        chart: ['Chart'],
      }
    } catch (error) {
      this.error = (error as ErrorEvent).message
    }
  }

  /**
   * TODO: move it to utils
   * @param children
   * @returns {*}
   */
  getRemapChildrenToDropdown = children => {
    return children.map(child => ({
      id: child.id,
      label: child.name,
      value: child.id,
    }))
  }

  setSelectedNodeId = nodeId => {
    this.selectedNodeId = nodeId
  }

  get selectedNode() {
    const node = this.findNode(this.selectedNodeId, this.reportBlueprint)
    return node || {}
  }

  setCurrentStep = step => {
    this.currentStep = step
  }

  /**
   * @param drilldownOption - { id: 'drilldown-id', label: 'Chart Title', value: 'drilldown-id' }
   * @param targetId - Chart | row's name | column's name
   */
  updateDrilldown = (drilldownOption, targetId) => {
    if (this.currentChart) {
      this.currentChart.drillDownInfo.linkages[targetId ?? '0'] = {
        chartName: drilldownOption.label,
        additionalFilters: [],
      }

      // force to update the structure
      this.reportBlueprint.children = [...this.reportBlueprint.children]
    }
  }

  findNode = (nodeId, structure) => {
    const node = structure.find(item => item.id === nodeId)
    if (node) return node
    for (let i = 0; i < structure.length; i++) {
      const item = structure[i]
      if (item.children) {
        const node = this.findNode(nodeId, item.children)
        if (node) return node
      }
    }
  }

  findDuplicatedDrilldownByName = (name, structure) => {
    const node = structure.find(item => item.name === name)
    if (node) return node
    for (let i = 0; i < structure.length; i++) {
      const item = structure[i]
      if (item.children) {
        const node = this.findDuplicatedDrilldownByName(name, item.children)
        if (node) return node
      }
    }
  }

  generateReport = async () => {
    try {
      this.savingStructure = true

      try {
        await generateReportSourceById(this.sourceId)
        this.saved = true
      } catch (error) {
        console.error(error)
      }
    } catch (e) {
      this.error = e
    } finally {
      this.savingStructure = false
    }
  }

  addNode = () => {
    runInAction(() => {
      this.reportBlueprint.children = [...this.reportBlueprint.children]
    })
  }

  setMetadata = metadata => {
    this.metadata = metadata
  }

  setReportTitle = value => {
    this.reportTitle = value
  }

  getBlueprintId = () => {
    return this.reportBlueprint.id
  }

  setSelectedBlueprint = blueprintId => (this.selectedBlueprint = blueprintId)

  get selectedBlueprintId() {
    return this.selectedBlueprint
  }

  setDrilldownMode = drilldownMode => {
    this.drilldownModeSelected = drilldownMode
    this.currentChart.drillDownInfo.type = drilldownMode.type
    this.currentChart.drillDownInfo.linkages = {}
  }

  updateReportBlueprint = children => {
    this.reportBlueprint = {
      ...this.reportBlueprint,
      children,
    }
  }
}

export default WizardStore
